import React, { useState, useEffect } from 'react'
import LocalHelmet from '../common/LocalHelmet'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { HOSTED_IMAGES } from '../utils/imageHosting'

const { FiCamera, FiExternalLink, FiImage, FiHeart } = FiIcons

const Attributions = () => {
  const [attributions, setAttributions] = useState([])

  useEffect(() => {
    // Collect all attributions from various sources
    const collectAttributions = () => {
      const allAttributions = []

      // Static hero overrides - now using GitHub hosted images
      const heroOverrides = {
        Philadelphia: {
          imageUrl: HOSTED_IMAGES.heroes.philadelphia,
          alt: 'Philadelphia City Hall at Night',
          photographerName: 'Zhifei Zhou',
          photographerUrl: 'https://unsplash.com/@zhifei',
          unsplashLink:
            'https://unsplash.com/photos/philadelphia-skyline-during-daytime-U3aF7hgUSrk',
        },
        'New York': {
          imageUrl: HOSTED_IMAGES.heroes.newyork,
          alt: 'New York City Times Square',
          photographerName: 'Luca Bravo',
          photographerUrl: 'https://unsplash.com/@lucabravo',
          unsplashLink:
            'https://unsplash.com/photos/new-york-skyline-during-daytime-O453M2Liufs',
        },
        'Los Angeles': {
          imageUrl: HOSTED_IMAGES.heroes.losangeles,
          alt: 'Los Angeles skyline with Griffith Observatory',
          photographerName: 'Chait Goli',
          photographerUrl: 'https://unsplash.com/@chaitgoli',
          unsplashLink:
            'https://unsplash.com/photos/los-angeles-skyline-during-daytime-fe8QFA7q5ss',
        },
        'San Francisco': {
          imageUrl: HOSTED_IMAGES.heroes.sanfrancisco,
          alt: 'San Francisco Cable Cars',
          photographerName: 'Maarten van den Heuvel',
          photographerUrl: 'https://unsplash.com/@mvdheuvel',
          unsplashLink:
            'https://unsplash.com/photos/golden-gate-bridge-san-francisco-8EzNkvLQosk',
        },
        Birmingham: {
          imageUrl: HOSTED_IMAGES.heroes.birmingham,
          alt: 'Birmingham the Magic City',
          photographerName: 'Test Override',
          photographerUrl: 'https://unsplash.com/@test',
          unsplashLink: 'https://unsplash.com/photos/birmingham-test',
        },
        London: {
          imageUrl: HOSTED_IMAGES.heroes.london,
          alt: 'London skyline with Big Ben',
          photographerName: 'Benjamin Davies',
          photographerUrl: 'https://unsplash.com/@bendavisual',
          unsplashLink:
            'https://unsplash.com/photos/london-skyline-Oja2ty_9ZLM',
        },
        Chicago: {
          imageUrl: HOSTED_IMAGES.heroes.chicago,
          alt: 'Chicago River Architecture',
          photographerName: 'Pedro Lastra',
          photographerUrl: 'https://unsplash.com/@peterlaster',
          unsplashLink:
            'https://unsplash.com/photos/chicago-skyline-during-daytime-Nyvq2juw4_o',
        },
      }

      // Add hero override attributions
      Object.entries(heroOverrides).forEach(([city, data]) => {
        if (data.photographerName && data.photographerUrl) {
          allAttributions.push({
            id: `hero-${city.toLowerCase()}`,
            imageUrl: data.imageUrl,
            alt: data.alt,
            photographerName: data.photographerName,
            photographerUrl: data.photographerUrl,
            unsplashLink: data.unsplashLink,
            usage: `${city}`,
            category: 'Hero Images',
          })
        }
      })

      // Static page images - now using GitHub hosted images
      const staticImages = [
        {
          id: 'home-hero-fallback',
          imageUrl: HOSTED_IMAGES.heroes.default,
          alt: 'Strategic planning meeting',
          photographerName: 'You X Ventures',
          photographerUrl: 'https://unsplash.com/@youxventures',
          unsplashLink:
            'https://unsplash.com/photos/strategic-planning-meeting-Oalh2MojUuk',
          usage: 'Default hero image fallback',
          category: 'Hero Images',
        },
        {
          id: 'case-for-change-analytics',
          imageUrl: HOSTED_IMAGES.caseForChange.analytics,
          alt: 'Business analytics and data visualization',
          photographerName: 'Carlos Muza',
          photographerUrl: 'https://unsplash.com/@kmuza',
          unsplashLink:
            'https://unsplash.com/photos/business-analytics-hpjSkU2UYSU',
          usage: 'Case for Change page hero',
          category: 'Page Content',
        },
        {
          id: 'case-for-change-timeline-1',
          imageUrl: HOSTED_IMAGES.caseForChange.dataAnalysis,
          alt: 'Data analysis workspace',
          photographerName: 'Stephen Dawson',
          photographerUrl: 'https://unsplash.com/@srd844',
          unsplashLink:
            'https://unsplash.com/photos/data-analysis-qwtCeJ5cLYs',
          usage: 'Case for Change - Current urgency',
          category: 'Page Content',
        },
        {
          id: 'case-for-change-timeline-3',
          imageUrl: HOSTED_IMAGES.caseForChange.growth,
          alt: 'Strategic business growth',
          photographerName: 'Isaac Smith',
          photographerUrl: 'https://unsplash.com/@isaacmsmith',
          unsplashLink:
            'https://unsplash.com/photos/business-growth-6EnTPvPPL6I',
          usage: 'Case for Change - Future timeline',
          category: 'Page Content',
        },
        {
          id: 'ai-measurement-hero',
          imageUrl: HOSTED_IMAGES.articles.aiMeasurementFeatured,
          alt: 'AI and technology visualization',
          photographerName: 'Unsplash Photographer',
          photographerUrl: 'https://unsplash.com/',
          unsplashLink: 'https://unsplash.com/photos/ai-technology-featured',
          usage: 'AI Measurement Revolution article',
          category: 'Article Images',
        },
      ]

      allAttributions.push(...staticImages)

      return allAttributions
    }

    setAttributions(collectAttributions())
  }, [])

  const groupedAttributions = attributions.reduce((groups, attribution) => {
    const category = attribution.category || 'Other'
    if (!groups[category]) {
      groups[category] = []
    }
    groups[category].push(attribution)
    return groups
  }, {})

  return (
    <>
      {/* HEAD META */}
      <LocalHelmet prioritizeSeoTags>
        <title>Photo Credits & Attributions | Richardson Strategy Group</title>
        <meta
          name="description"
          content="See photo credits and image attributions for visuals and hero shots on Richardson Strategy Group's website. All used under Unsplash's license."
        />
        <link rel="canonical" href="https://richardsonstrategy.group/attributions" />

        {/* Open Graph */}
        <meta property="og:site_name" content="Richardson Strategy Group" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Photo Credits & Attributions | Richardson Strategy Group"
        />
        <meta
          property="og:description"
          content="Full photo credits and attributions for every visual used across RSG's site. All images credited to Unsplash photographers."
        />
        <meta property="og:url" content="https://richardsonstrategy.group/attributions" />
        <meta
          property="og:image"
          content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/og-default.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Photo Credits & Attributions | Richardson Strategy Group"
        />
        <meta
          name="twitter:description"
          content="Find every photo credit and image attribution for visuals seen on Richardson Strategy Group's website."
        />
        <meta
          name="twitter:image"
          content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/og-default.jpg"
        />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Photo Credits & Attributions',
            url: 'https://richardsonstrategy.group/attributions',
            description:
              'Photo credits and attributions for RSG website images—full Unsplash compliance and photographer acknowledgments.',
          })}
        </script>
      </LocalHelmet>

      <div className="min-h-screen py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center justify-center space-x-3 mb-6"
            >
              <SafeIcon icon={FiCamera} className="w-8 h-8 text-primary-600" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-900">
                Photo Credits & Attributions
              </h1>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-secondary-600 mb-6 max-w-3xl mx-auto"
            >
              We're grateful to the talented photographers on Unsplash who make our website beautiful. 
              All images are used in compliance with Unsplash's license terms.
            </motion.p>
            
            {/* Unsplash Attribution */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-primary-50 inline-flex items-center space-x-3 px-6 py-4 rounded-xl border border-primary-200"
            >
              <SafeIcon icon={FiHeart} className="w-6 h-6 text-red-500" />
              <div className="text-left">
                <p className="text-secondary-900 font-semibold">Powered by</p>
                <a
                  href="https://unsplash.com/?utm_source=richardson-strategy-group&utm_medium=referral"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 font-medium transition-colors inline-flex items-center space-x-1"
                >
                  <span>Unsplash</span>
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>

          {/* Attribution Groups */}
          <div className="space-y-12">
            {Object.entries(groupedAttributions).map(([category, items], categoryIndex) => (
              <motion.section
                key={category}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              >
                <div className="flex items-center space-x-3 mb-6">
                  <SafeIcon icon={FiImage} className="w-6 h-6 text-primary-600" />
                  <h2 className="text-2xl font-bold text-secondary-900">{category}</h2>
                  <div className="flex-1 h-px bg-secondary-200"></div>
                  <span className="text-sm text-secondary-500 bg-secondary-100 px-3 py-1 rounded-full">
                    {items.length} image{items.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map((attribution, index) => (
                    <motion.div
                      key={attribution.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: categoryIndex * 0.1 + index * 0.05 }}
                      className="bg-white rounded-xl shadow-sm border border-secondary-100 overflow-hidden hover:shadow-md transition-shadow"
                    >
                      {/* Image Preview */}
                      <div className="aspect-video bg-secondary-100 overflow-hidden">
                        <img
                          src={attribution.imageUrl}
                          alt={attribution.alt}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>

                      {/* Attribution Details */}
                      <div className="p-4 space-y-3">
                        <div>
                          <h3 className="font-semibold text-secondary-900 text-sm mb-1">
                            {attribution.alt}
                          </h3>
                          <p className="text-xs text-secondary-500">
                            {attribution.usage}
                          </p>
                        </div>

                        {/* Photographer Credit */}
                        <div className="bg-secondary-50 p-3 rounded-lg">
                          <p className="text-xs text-secondary-600 mb-2">Photo by</p>
                          <div className="flex items-center justify-between">
                            <a
                              href={attribution.photographerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors inline-flex items-center space-x-1"
                            >
                              <span>{attribution.photographerName}</span>
                              <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                            </a>
                            <span className="text-xs text-secondary-500">on</span>
                            <a
                              href={
                                attribution.unsplashLink ||
                                'https://unsplash.com/?utm_source=richardson-strategy-group&utm_medium=referral'
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors"
                            >
                              Unsplash
                            </a>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            ))}
          </div>

          {/* License Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-16 bg-secondary-50 p-6 md:p-8 rounded-2xl border border-secondary-200"
          >
            <h3 className="text-xl font-bold text-secondary-900 mb-4">License Information</h3>
            <div className="prose prose-secondary max-w-none">
              <p className="text-secondary-600 mb-4">
                All images sourced from Unsplash are used under the{' '}
                <a
                  href="https://unsplash.com/license"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Unsplash License
                </a>
                , which allows free use for commercial and non-commercial purposes.
              </p>
              <div className="bg-white p-4 rounded-lg border border-secondary-200">
                <h4 className="font-semibold text-secondary-900 mb-2">Attribution Requirements:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-secondary-600">
                  <li>Photographer credit with link to their Unsplash profile</li>
                  <li>Reference to Unsplash as the source platform</li>
                  <li>Compliance with Unsplash's community guidelines</li>
                  <li>Respect for photographer's moral rights</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Footer Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mt-12"
          >
            <p className="text-sm text-secondary-500">
              If you're a photographer featured here and have any questions or concerns, 
              please{' '}
              <a
                href="mailto:info@richardsonstrategy.group"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                contact us
              </a>
              .
            </p>
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default Attributions